import React, { useEffect, useState } from 'react';
import DyanamicCollapseReportTable from './ManageProjectsTable';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  OverlayTrigger,
  Popover,
  Row,
  Tooltip
} from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import useApi from 'hooks/useApi';
import {
  getExportAllData,
  getFarmTableData
} from 'api/endpoints/TerrestrialServices';
import { get } from 'lodash';
import { jwtDecode } from 'jwt-decode';
import { isEmpty } from 'lodash';
import { faDownload, faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomSpinner from 'components/common/CustomSpinner';
import * as XLSX from 'xlsx';
import CardDropdown from 'components/common/CardDropdown';
import { getinfectionReport } from 'api/endpoints/CropInfection';
import { useNavigate } from 'react-router-dom';
import { getTenantFromToken } from 'utils/routeUtils';
import { toast } from 'react-toastify';

function DynamicCollapseReport({
  title,
  apiPath,
  fileName,
  dropdownFilter,
  cropFilter,
  responsive = false,
  cardTittles = [],
  globalSearch = true,
  dateRangePicker,
  forInfection,
  forCropCalendar,
  buttons,
  service,
  selection
}) {
  const tenant = getTenantFromToken();
  const getExportAllApi = useApi(getExportAllData);
  const navigate = useNavigate();

  const getInfectionReportApi = useApi(getinfectionReport);

  const [showFilter, setShowFilter] = useState(false);
  const [exportdata, setExportData] = useState(null);

  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);

  const [exportKey, setExportKey] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const handleFilterToggle = () => {
    setShowFilter(!showFilter);
  };

  // Update block list when blocks API data changes
  useEffect(() => {
    const data = get(getExportAllApi, 'data.results', []);
    if (!isEmpty(data)) {
      const s3Link = data; // Assuming the S3 link is in the first block
      downloadFile(s3Link);
    }
  }, [getExportAllApi.data]);

  // Function to download the file from S3 link
  const downloadFile = url => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const formatOptimalValue = rawVal => {
    if (typeof rawVal !== 'string') return rawVal ?? '';
    const match =
      rawVal.match(/^([\d.]+)[^\d(]*\(\s*Optimal:\s*(.*?)\)/i) ||
      rawVal.match(/^([\d.]+)[^\d(]*\(\s*(.*?)\)/);
    if (match) {
      const [, value, optimal] = match;
      const cleanOptimal = optimal.replace(/[^\d.\-: ]+/g, '').trim();
      return `${value} (Optimal: ${cleanOptimal})`;
    }
    // If it looks like a numeric range with units (e.g., "28.00 - 26.00 (°C)")
    const rangeMatch = rawVal.match(/^([\d.]+)\s*-\s*([\d.]+)\s*\((.*?)\)$/);
    if (rangeMatch) {
      return `${rangeMatch[1]} - ${rangeMatch[2]}`;
    }
    // Otherwise, return the raw string (e.g., "Sugarcane")
    return rawVal.trim();
  };

  const flattenObject = (obj, prefix = '') => {
    const result = {};

    Object.entries(obj || {}).forEach(([key, val]) => {
      const rawVal = Array.isArray(val) ? val[0] : val;

      if (key === 'Weather Parameters' && typeof rawVal === 'object') {
        Object.entries(rawVal).forEach(([weatherKey, weatherVal]) => {
          const unitMatch = weatherVal.match(/\((.*?)\)/);
          const rangeMatch = weatherVal.match(/^([\d.]+)\s*-\s*([\d.]+)/);
          const cleanUnit = unitMatch ? unitMatch[1] : '';
          const cleanRange = rangeMatch
            ? `${rangeMatch[1]} - ${rangeMatch[2]}`
            : weatherVal;

          const newKey = `${weatherKey} (${cleanUnit})`;
          result[newKey] = cleanRange;
        });
      } else if (
        rawVal &&
        typeof rawVal === 'object' &&
        !Array.isArray(rawVal)
      ) {
        Object.assign(result, flattenObject(rawVal, key));
      } else {
        result[key] = formatOptimalValue(rawVal);
      }
    });

    return result;
  };

  const convertToExcelData = (data, fileName) => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const now = new Date()
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      .replace(',', ' at');

    const readableName = fileName
      .replace('.xlsx', '')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());

    const reportTitle = `${readableName} – Generated on ${now} from the FARMSIO Application`;

    const excludeKeys = ['id', 'action', 'modalButtons', 'extra', 'more'];
    const allKeys = new Set();
    const rows = [];

    data.forEach(row => {
      const base = {};
      // Step 1: Base row keys
      Object.entries(row).forEach(([k, v]) => {
        if (!excludeKeys.includes(k)) {
          base[k] = v?.label ?? v;
          allKeys.add(k);
        }
      });

      // Step 2: Flatten `more.data`
      const moreFlat = flattenObject(row.more?.data);
      Object.entries(moreFlat).forEach(([k, v]) => {
        base[k] = v;
        allKeys.add(k);
      });

      // Step 3: Flatten `extra.data` (array of objects)
      const extras = Array.isArray(row.extra?.data) ? row.extra.data : [{}];
      extras.forEach(extra => {
        const extraFlat = flattenObject(extra);
        const combined = { ...base, ...extraFlat };
        Object.keys(extraFlat).forEach(k => allKeys.add(k));
        rows.push(combined);
      });

      // If no extras, still push base
      if (!row.extra?.data?.length) {
        rows.push(base);
      }
    });

    const headerKeys = Array.from(allKeys);
    const headers = headerKeys.map(k =>
      k.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    );
    const dataRows = rows.map(row => headerKeys.map(k => row[k] ?? ''));

    return [[reportTitle], [], headers, ...dataRows];
  };

  // Handle download
  const handleDownload = () => {
    const excelData = convertToExcelData(exportdata, fileName);
    downloadExcel(excelData, fileName);
  };

  // Download logic
  const downloadExcel = (excelData, filename) => {
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    const sheetName = filename.replace('.xlsx', '');
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, filename);
  };

  const handleExportClick = event => {
    setShow(!show);
    setTarget(event.target);
  };

  const handleDownloadAll = () => {
    // Your download all logic here
    let tenant_id = tenant;
    getExportAllApi.request(tenant_id);
  };

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Update windowWidth on window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    // Clean up the event listener when the component is unmounted
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div>
      <Row className="d-flex justify-content-end mb-2">
        <Col className="text-end">
          {buttons
            ?.filter(button => button.enabled)
            .map((button, index) => (
              <Button
                key={index}
                variant="primary"
                size="sm"
                onClick={() => {
                  if (
                    button.action === 'multi_transfer' ||
                    button.action === 'CREATE_TRANSFER' ||
                    button.action === 'multi_reception' ||
                    button.action === 'CREATE_RECEPTION'
                  ) {
                    if (!selectedRows || selectedRows.length === 0) {
                      toast.warn(
                        `Please select at least one item to ${
                          button.action.includes('RECEPTION')
                            ? 'receive'
                            : 'transfer'
                        }.`
                      );
                      return;
                    }
                    if (selectedRows.length > 1) {
                      const firstCrop = selectedRows[0].Crop;
                      const firstVariety = selectedRows[0].Variety;

                      const isConsistent = selectedRows.every(
                        row =>
                          row.Crop === firstCrop && row.Variety === firstVariety
                      );

                      if (!isConsistent) {
                        toast.error(
                          'Selected procurements must have the same Crop and Variety.',
                          {
                            style: {
                              backgroundColor: '#e63757',
                              color: 'white',
                              fontWeight: 'bold'
                            },
                            icon: true
                          }
                        );
                        return;
                      }
                    }

                    const ids = selectedRows
                      .map(r => r.id || r['Procurement ID'])
                      .filter(Boolean)
                      .join(',');
                    if (
                      button.action === 'multi_reception' ||
                      button.action === 'CREATE_RECEPTION'
                    ) {
                      navigate(
                        `/${tenant}${button.redirectPath}?transfers=${ids}`
                      );
                    } else {
                      navigate(
                        `/${tenant}${button.redirectPath}?procurements=${ids}`
                      );
                    }
                  } else {
                    navigate(`/${tenant}${button.redirectPath}`);
                  }
                }}
                className={index > 0 ? 'ms-2' : ''} // Apply spacing only to subsequent buttons
              >
                {button.buttonName}
              </Button>
            ))}
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <FalconCardHeader
              title={title}
              titleTag="h5"
              light={true}
              endEl={
                <div className="d-flex justify-content-end my-auto">
                  {windowWidth >= 768 && (
                    <div
                      className="px-2 my-auto theme-control-toggle"
                      onClick={handleFilterToggle}
                    >
                      <OverlayTrigger
                        key="filter"
                        placement={'bottom'}
                        overlay={<Tooltip id="FilterData">Filter</Tooltip>}
                      >
                        <div className="theme-control-toggle-label">
                          <FontAwesomeIcon icon={faFilter} className="fs-0" />
                        </div>
                      </OverlayTrigger>
                    </div>
                  )}
                  <CardDropdown
                    onExport={handleDownload}
                    exportKey={exportKey}
                  />
                </div>
              }
            />
            <CardBody>
              {getInfectionReportApi.loading ? (
                <CustomSpinner />
              ) : (
                <DyanamicCollapseReportTable
                  // tableData={tableData.data}
                  key={apiPath}
                  hyperlink={false}
                  showFilter={showFilter}
                  setExportData={setExportData}
                  apiPath={apiPath}
                  responsive={responsive}
                  cardTittles={cardTittles}
                  globalSearchFilter={globalSearch}
                  dropdownFilter={dropdownFilter}
                  cropFilter={cropFilter}
                  dateRangePicker={dateRangePicker}
                  forInfection={forInfection}
                  service={service}
                  forCropCalendar={forCropCalendar}
                  setExportKey={setExportKey}
                  selection={selection}
                  onSelectionChange={setSelectedRows}
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

DynamicCollapseReport.propTypes = {
  apiPath: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  dropdownFilter: PropTypes.bool,
  forInfection: PropTypes.bool,
  forCropCalendar: PropTypes.bool,
  errorMsg: PropTypes.string,
  editPath: PropTypes.string,
  addPath: PropTypes.string,
  editKey: PropTypes.string,
  forWeather: PropTypes.bool,
  service: PropTypes.string,
  selection: PropTypes.bool,
  dateFilter: PropTypes.bool,
  dateRangePicker: PropTypes.bool,
  cropFilter: PropTypes.bool,
  globalSearch: PropTypes.bool,
  showHeader: PropTypes.bool,
  showFooter: PropTypes.bool,
  limit: PropTypes.number,
  district_ids: PropTypes.arrayOf(PropTypes.number),
  responsive: PropTypes.bool,
  cardTittles: PropTypes.arrayOf(PropTypes.string),
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      enabled: PropTypes.bool,
      buttonName: PropTypes.string,
      redirectPath: PropTypes.string
    })
  )
};

export default DynamicCollapseReport;
